<script>
import fromPairs from 'lodash/fromPairs'
import date from 'locutus/php/datetime/date'
import AdminLayout from '@layouts/admin-main'
import ContractNav from '@components/admin/contract-nav'
import FormInfo from '@components/admin/contract-form-info'
import DocumentInfo from '@components/admin/contract-document-info'
import { mapState } from 'vuex'
import {
  fetchForms,
  deleteForm,
  fetchDocuments,
  deleteDocument,
} from '@apis/admin/contract'

export default {
  page: {
    title: 'Admin::Contract',
    meta: [{ name: 'description', content: 'Admin::Contract' }],
  },
  components: {
    AdminLayout,
    ContractNav,
    FormInfo,
    DocumentInfo,
  },
  data() {
    return {
      forms: [],
      target_form_seq: 0,
      show_form_info: false,
      documents: [],
      target_document_seq: 0,
      show_document_info: false,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadForms(), this.loadDocuments()]).catch((e) =>
        this.$toasted.error(e.message)
      )
    },
    async loadForms() {
      const { data } = await fetchForms()

      this.forms = data
    },
    async loadDocuments() {
      const { data } = await fetchDocuments()

      this.documents = data
    },
    handleCreateForm() {
      this.show_form_info = true
      this.target_form_seq = null
    },
    handleUpdateForm({ seq }) {
      this.show_form_info = true
      this.target_form_seq = seq
    },
    handleFormSubmit(seq) {
      this.init()
      this.target_form_seq = seq
    },
    async handleDeleteForm(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteForm(seq)
        await this.loadForms()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    handleCreateDocument() {
      this.show_document_info = true
      this.target_document_seq = null
    },
    handleUpdateDocument({ seq }) {
      this.show_document_info = true
      this.target_document_seq = seq
    },
    handleDocumentSubmit(seq) {
      this.init()
      this.target_document_seq = seq
    },
    async handleDeleteDocument(seq) {
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteDocument(seq)
        await this.loadDocuments()
      } catch (e) {
        this.$toasted.error(e.message)
      }
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <AdminLayout>
    <ContractNav></ContractNav>
    <div :class="$style.adminLeftContent">
      <h3 :class="$style.xeAdmin">{{
        $t('contract_catalog.managing_contract_documents')
      }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('contract_catalog.name') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('contract_catalog.title') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('contract_catalog.document_type') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('contract_catalog.mainly_locale') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('contract_catalog.registration_date') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateDocument"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="document in documents" :key="document.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{
              document.name
            }}</td>
            <td :class="[$style.wide]">{{ document.title }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              fromPairs(constants.contract.document.document_type)[
                document.document_type
              ]
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]"
              >{{
                fromPairs(constants.lang.locales)[document.master_locale]
              }}
              [{{ document.master_locale }}]</td
            >
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(document.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button]"
                @click.prevent="handleUpdateDocument(document)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteDocument(document.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!documents.length">
            <td :colspan="6" :class="$style.tCenter">{{
              $t('contract_catalog.msg_no_document')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div :class="$style.adminRightExtra">
      <h3 :class="$style.xeAdmin">{{
        $t('contract_catalog.managing_contract_forms')
      }}</h3>
      <table cellspacing="0" cellpadding="0" :class="$style.crossTable">
        <thead>
          <tr>
            <th scope="col"
              ><div>{{ $t('contract_catalog.name') }}</div></th
            >
            <th scope="col"
              ><div>{{ $t('contract_catalog.title') }}</div></th
            >
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.button, $style.black]"
                  @click.prevent="handleCreateForm"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="form in forms" :key="form.seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ form.name }}</td>
            <td :class="[$style.wide]">{{ form.title }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                href="#"
                :class="[$style.button]"
                @click.prevent="handleUpdateForm(form)"
                ><span>{{ $t('admin.modify') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="handleDeleteForm(form.seq)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!forms.length">
            <td :colspan="3" :class="$style.tCenter">{{
              $t('contract_catalog.msg_no_forms')
            }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <ElDialog
      :title="
        target_document_seq
          ? $t('contract_catalog.modifying_document')
          : $t('contract_catalog.register_document')
      "
      :visible.sync="show_document_info"
    >
      <DocumentInfo
        v-if="show_document_info"
        :key="target_document_seq"
        :document_seq="target_document_seq"
        @submit="handleDocumentSubmit"
      ></DocumentInfo>
    </ElDialog>
    <ElDialog
      :title="
        target_form_seq
          ? $t('contract_catalog.modifying_forms')
          : $t('contract_catalog.register_forms')
      "
      :visible.sync="show_form_info"
    >
      <FormInfo
        v-if="show_form_info"
        :key="target_form_seq"
        :form_seq="target_form_seq"
        @submit="handleFormSubmit"
      ></FormInfo>
    </ElDialog>
  </AdminLayout>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
